import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DayOfWeek } from '@weareopenr/types';
import {
  TimetableScheduleBlock,
  TimetableScheduleNormalizationError,
} from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import {
  BreadcrumbHeading,
  Chip,
  DuplicateIcon,
  Export,
  EditIcon,
  Info,
  Popover,
  PrimaryButton,
  Prompt,
  RadioSelect,
  SecondaryButton,
  SnackbarStatus,
  Spinner,
  TextIcon,
  Typography,
  useSnackbar,
  useTray,
  ViewIcon,
  Bin,
  ValidationIcon,
} from '@weareopenr/component-library';
import { ValidationStatus } from 'src/api/getSchedules/types';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { VALIDATION_POLL_INTERVAL_MS } from 'src/hooks/useSchedules/useSchedules';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { ValidationReportContainer } from 'src/components/ValidationReportContainer';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { capitalise } from 'src/helpers/capitalise';
import { useTimetable } from 'src/hooks/useTimetable';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { usePermissions } from 'src/hooks/usePermissions/usePermissions';
import { useDeleteTimetablePrompt } from 'src/hooks/useDeleteTimetablePrompt';
import { useUpdateTimetable } from 'src/hooks/useUpdateTimetable';
import { useRemoveFromTimetablePrompt } from 'src/hooks/useRemoveFromTimetablePrompt';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { TimetableDay } from 'src/components/TimetableDay';
import TimetablePublishModal from 'src/components/TimetablePublishModal';
import { DeleteEntityPrompts } from 'src/components/DeleteEntityPrompts';
import { AddScheduleFormValues, AddScheduleTrayContent, ManagedSchedule } from 'src/components/AddScheduleTrayContent';
import {
  DuplicateTimetableTrayContent,
  getNextOpenId,
  ICloseDuplicateTrayOptions,
} from 'src/components/DuplicateTimetableTray';
import { RenameTimetableTrayContent } from 'src/components/RenameTimetableTray';
import {
  ICloseTimetableDetailsTrayOptions,
  ITimetableDetailsPreview,
  TimetableDetailsTrayContent,
} from 'src/components/TimetableDetailsTray';
import { TimetableEntitiesTrayContent } from 'src/components/TimetableEntitiesTray';
import { useMenus } from 'src/hooks/useMenus';
import { useTimetableMenuSets } from 'src/hooks/useTimetableMenuSets';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { ITimetableDetail, ITimetableResponseBody, ITimetableSchedule } from 'src/api/getTimetable';
import { IMenu } from 'src/types/menu.types';
import { IMenuSet } from 'src/api/getMenuSets';
import { IPriceScheme } from 'src/types/price.types';
import { addScheduleOptimistically, AddScheduleInput } from 'src/utils/timetables/addScheduleOptimistically';
import { getChangedScheduleIds } from 'src/utils/timetables/getChangedScheduleIds';
import { buildTimetableDetailsPreviewSchedules } from 'src/utils/timetables/buildTimetableDetailsPreviewSchedules';
import { getInvalidDefaultScheduleIds } from 'src/utils/timetables/getInvalidDefaultScheduleIds';
import { getDefaultStatusChangedScheduleIds } from 'src/utils/timetables/getDefaultStatusChangedScheduleIds';
import { ITimetableDaySchedule } from 'src/utils/timetables/groupSchedulesByDay';
import { buildRemovalBlocks, RemovalTarget } from 'src/utils/timetables/buildRemovalBlocks';
import {
  filterMenusByRanges,
  filterMenuSetsByRanges,
  getDefaultEntity as getDefaultEntityRef,
  getTrack,
  TimetableEntityType,
  TimetableScheduleTrack,
} from 'src/utils/timetables/helpers';
import styles from './Timetable.module.scss';

// What a track holds, for the add tray's title and the default a removal falls back to.
const TRACK_LABELS: Record<TimetableScheduleTrack, string> = {
  menu: 'menu',
  price_scheme: 'price scheme',
};

const ENTITY_LABELS: Record<TimetableEntityType, string> = {
  menu: 'menu',
  menuSet: 'menu set',
  priceScheme: 'price scheme',
};

const ADD_SCHEDULE_ERROR_MESSAGES: Record<TimetableScheduleNormalizationError, string> = {
  [TimetableScheduleNormalizationError.InvalidStartTime]: 'Enter a valid start time.',
  [TimetableScheduleNormalizationError.InvalidEndTime]: 'Enter a valid end time.',
  [TimetableScheduleNormalizationError.MissingEntity]: 'Select a menu, menu set or price scheme.',
  [TimetableScheduleNormalizationError.ConflictingEntity]:
    'A schedule can only have one menu, menu set or price scheme.',
  [TimetableScheduleNormalizationError.DuplicateStartTime]: 'Another schedule on this day already starts at that time.',
  // addScheduleOptimistically always builds a contiguous 00:00-23:59 day itself, so this should
  // be unreachable in practice - kept as a defensive fallback rather than an uncaught crash.
  [TimetableScheduleNormalizationError.NonContiguousSchedule]: 'Something went wrong building this schedule.',
};

// Any time left uncovered by the chosen start/end falls back to the timetable's default menu or
// price scheme - see addScheduleOptimistically.
const getDefaultEntity = (
  track: TimetableScheduleTrack,
  timetable: ITimetableDetail,
  menus: IMenu[],
  menuSets: IMenuSet[],
  priceSchemes: IPriceScheme[],
): { id: string; type: TimetableEntityType; name: string } => {
  const { id, type } = getDefaultEntityRef(timetable, track);
  if (type === 'menuSet') {
    return { id, type, name: menuSets.find((menuSet) => menuSet.menu_set_id === id)?.name ?? 'Default menu set' };
  }
  if (type === 'menu') {
    return { id, type, name: menus.find((menu) => menu.menuId === id)?.name ?? 'Default menu' };
  }
  return {
    id,
    type,
    name: priceSchemes.find((priceScheme) => priceScheme.id === id)?.name ?? 'Default price scheme',
  };
};

const getRemovePromptCopy = (target: RemovalTarget) => {
  if (target.kind === 'timeslot') {
    const { name, day_of_week, start_time, end_time } = target.schedule;
    return {
      heading: `Are you sure you want to remove the ${name} time slot?`,
      buttonText: 'Remove time slot',
      errorMessage: 'Something went wrong whilst removing the time slot.',
      body: `Remove ${name} time slot from ${capitalise(day_of_week)}, ${start_time} - ${end_time}. Any gaps in the day will be filled by your default menu and price scheme.`,
    };
  }
  const { name, entityType } = target.group;
  const label = ENTITY_LABELS[entityType];
  return {
    heading: `Are you sure you want to remove the “${name}”?`,
    buttonText: `Remove ${label}`,
    errorMessage: `Something went wrong whilst removing the ${label}.`,
    body: (
      <>
        This action will remove all instances of the {label} on the timetable, and replace it with the default{' '}
        {TRACK_LABELS[getTrack(entityType)]}.
        <br />
        This will <u className={styles['emphasis']}>not</u> delete the {label} from the brand.
      </>
    ),
  };
};

export interface ITimetableLocationState {
  openDetailsTray?: boolean;
}

export enum TimetableView {
  MENUS_AND_PRICING = 'MENUS_AND_PRICING',
  MENUS_ONLY = 'MENUS_ONLY',
  PRICING_ONLY = 'PRICING_ONLY',
}

const DELETE_ERROR_MESSAGE = 'Something went wrong whilst deleting the timetable';

const TIMETABLE_VIEW_OPTIONS = [
  { id: TimetableView.MENUS_AND_PRICING, label: 'Menus & Pricing' },
  { id: TimetableView.MENUS_ONLY, label: 'Menus only' },
  { id: TimetableView.PRICING_ONLY, label: 'Pricing only' },
];

// The details tray can rewrite affected schedules too (see buildTimetableDetailsPreviewSchedules),
// so its snapshot needs to cover schedules as well as the defaults/ranges it directly edits.
type TimetableDetailsSnapshot = ITimetableDetailsPreview & { schedules: ITimetableSchedule[] };

interface TimetableTooltipProps {
  label: string;
  children: ReactNode;
}

const TimetableTooltip = ({ label, children }: TimetableTooltipProps) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <button type="button" aria-label={label}>
        <Info />
      </button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="start" sideOffset={4}>
      <div className={styles['tooltip']}>
        <Typography colour="secondary" size="small">
          {children}
        </Typography>
      </div>
    </Popover.Content>
  </Popover.Root>
);

export const Timetable = () => {
  const { timetableId } = useParams<{ timetableId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [timetableView, setTimetableView] = useState(TimetableView.MENUS_AND_PRICING);

  const { openTray, closeTray, isTrayOpen } = useTray();
  // Poll while a validation runs so Validate and View Validation Report update when it finishes - but
  // never while a tray is open, as the tray's live preview is written into this query's cache
  const { data, byDay, isLoading } = useTimetable(timetableId, {
    refetchInterval: (response) =>
      !isTrayOpen && response?.data.validation?.status === ValidationStatus.VALIDATING
        ? VALIDATION_POLL_INTERVAL_MS
        : false,
  });
  const timetable = data?.data;
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { canCreateTimetables, canUpdateTimetables, canPublishTimetables } = usePermissions();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { captureException } = useUserTracking();
  const validate = useValidate();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const validationStatus = timetable?.validation?.status;
  const validationReportId = timetable?.validation?.report_id ?? null;
  const isValidationInProgress = validate.isLoading || validationStatus === ValidationStatus.VALIDATING;
  // Only failed runs have a report to review - a clean run has nothing to show
  const canViewValidationReport =
    Boolean(validationReportId) && validationStatus === ValidationStatus.VALIDATION_FAILED;

  // No modal: timetables are validated against their own price schemes, so there's nothing to choose
  const handleValidate = async () => {
    if (!timetableId) return;
    try {
      await validate.mutateAsync({
        criteria: { timetable_id: timetableId, rules: getValidationRules(EVENT_TYPES.PUBLISH_TIMETABLE) },
      });
      showSnackbar({ status: SnackbarStatus.PROGRESS, message: 'Your timetable validation is in progress' });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetable, getBrandId(), timetableId] });
    } catch (err) {
      captureException(err, { tags: { action: 'validate_timetable', timetableId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to validate timetable. Please try again.' });
    }
  };
  const { mutateAsync: submitTimetableUpdate } = useUpdateTimetable(timetableId);
  const { data: menus = [], isLoading: isMenusLoading } = useMenus();
  const { data: menuSets, isLoading: isMenuSetsLoading } = useTimetableMenuSets();
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();
  const menusInRange = filterMenusByRanges(menus, timetable?.ranges ?? []);
  const menuSetsInRange = filterMenuSetsByRanges(menuSets, timetable?.ranges ?? []);
  // The default menu/menu set/price scheme already covers any unscheduled time (see
  // addScheduleOptimistically), so it isn't offered as something to explicitly add.
  const selectableMenus = menusInRange.filter((menu) => menu.menuId !== timetable?.default_menu_id);
  const selectableMenuSets = menuSetsInRange.filter(
    (menuSet) => menuSet.menu_set_id !== timetable?.default_menu_set_id,
  );
  const selectablePriceSchemes = priceSchemes.filter(
    (priceScheme) => priceScheme.id !== timetable?.default_price_scheme_id,
  );

  // Snapshot of the schedules from just before the tray opened, so an uncommitted live preview
  // can be reverted if the user cancels, dismisses the tray, or navigates away without saving.
  const originalSchedulesRef = useRef<ITimetableSchedule[] | null>(null);
  const isCommittedRef = useRef(false);
  // Same idea as the pair above, but for the details tray's default menu/price scheme/ranges
  // preview rather than the add-schedule tray's schedules preview - the two trays are mutually
  // exclusive (only one can be open at a time), so they share `changedScheduleIds` below.
  const originalDetailsRef = useRef<TimetableDetailsSnapshot | null>(null);
  const isDetailsCommittedRef = useRef(false);
  // Bumped on every open so the tray always gets a fresh AddScheduleTrayContent instance - the
  // Tray keeps its content mounted while closing (no unmountOnExit), so reusing the same key
  // across opens would carry over the previous session's form state instead of resetting it.
  const trayInstanceIdRef = useRef(0);
  // Ids of schedules the current live preview (either tray) added, altered, or re-assigned as
  // the default, so their tiles can be highlighted with a dashed border while it's showing.
  const [changedScheduleIds, setChangedScheduleIds] = useState<Set<string>>(new Set());
  const saveScheduleBlocks = async (blocks: TimetableScheduleBlock[]) => {
    const response = await submitTimetableUpdate(blocks);
    queryClient.setQueryData([QueryKeys.timetable, getBrandId(), timetableId], response);
  };
  const {
    target: removalTarget,
    isOpen: isRemovePromptOpen,
    isSubmitting: isRemoving,
    open: openRemoval,
    close: closeRemovePrompt,
    confirm: confirmRemoval,
  } = useRemoveFromTimetablePrompt<RemovalTarget>({
    buildBlocks: (target) => timetable && buildRemovalBlocks(timetable, target),
    submit: saveScheduleBlocks,
    getErrorMessage: (target) => getRemovePromptCopy(target).errorMessage,
  });
  // Ids of schedules standing in for a default that's been cleared (e.g. it fell outside a newly
  // selected range) with nothing chosen to replace it yet - shown with a "(!) - " warning prefix
  // until the user picks a new default.
  const [invalidScheduleIds, setInvalidScheduleIds] = useState<Set<string>>(new Set());

  const writeSchedules = (schedules: ITimetableSchedule[]) => {
    queryClient.setQueryData<ITimetableResponseBody>(
      [QueryKeys.timetable, getBrandId(), timetableId],
      (current) => current && { ...current, data: { ...current.data, schedules } },
    );
  };

  const writeTimetableDetails = (details: ITimetableDetailsPreview) => {
    queryClient.setQueryData<ITimetableResponseBody>(
      [QueryKeys.timetable, getBrandId(), timetableId],
      (current) => current && { ...current, data: { ...current.data, ...details } },
    );
  };

  const revertUncommittedPreview = () => {
    if (originalSchedulesRef.current && !isCommittedRef.current) {
      writeSchedules(originalSchedulesRef.current);
    }
    originalSchedulesRef.current = null;
    isCommittedRef.current = false;
  };

  const revertUncommittedDetailsPreview = () => {
    if (originalDetailsRef.current && !isDetailsCommittedRef.current) {
      writeTimetableDetails(originalDetailsRef.current);
      writeSchedules(originalDetailsRef.current.schedules);
    }
    originalDetailsRef.current = null;
    isDetailsCommittedRef.current = false;
  };

  // Covers Cancel and the tray's own close button, both of which just flip `isTrayOpen`. Only
  // clears `changedScheduleIds` here, not in the unmount cleanup below, since setting state
  // during/after unmount would warn - the component won't render again anyway once it's gone.
  useEffect(() => {
    if (!isTrayOpen) {
      revertUncommittedPreview();
      revertUncommittedDetailsPreview();
      setChangedScheduleIds(new Set());
      setInvalidScheduleIds(new Set());
    }
  }, [isTrayOpen]);

  // Covers navigating away from the page entirely, while the tray is still open.
  useEffect(() => {
    return () => {
      revertUncommittedPreview();
      revertUncommittedDetailsPreview();
      closeTray();
    };
  }, []);

  // A menu track pick is a menu or a menu set - ids are unique across both, so an id the brand's
  // menu sets (or this timetable's menu set rows, for one since filtered out) know is a menu set.
  const getEntityTypeOf = (track: TimetableScheduleTrack, entityId: string): TimetableEntityType => {
    if (track === 'price_scheme') return 'priceScheme';
    const isMenuSet =
      menuSets.some((menuSet) => menuSet.menu_set_id === entityId) ||
      (timetable?.schedules ?? []).some((schedule) => schedule.menu_set_id === entityId);
    return isMenuSet ? 'menuSet' : 'menu';
  };

  // `managedScheduleId`: the entry being amended via "Manage time slot"; undefined when adding.
  const buildAddScheduleInput = (
    track: TimetableScheduleTrack,
    values: AddScheduleFormValues,
    managedScheduleId?: string,
  ): AddScheduleInput | null => {
    if (!timetable) return null;
    const defaultEntity = getDefaultEntity(track, timetable, menusInRange, menuSetsInRange, priceSchemes);
    return {
      entityType: getEntityTypeOf(track, values.entityId),
      ...values,
      defaultEntityId: defaultEntity.id,
      defaultEntityType: defaultEntity.type,
      defaultEntityName: defaultEntity.name,
      defaultMenuId: timetable.default_menu_id,
      defaultMenuSetId: timetable.default_menu_set_id ?? null,
      defaultPriceSchemeId: timetable.default_price_scheme_id,
      managedScheduleId,
    };
  };

  const handleAddSchedulePreview = (
    track: TimetableScheduleTrack,
    values: AddScheduleFormValues | null,
    managedScheduleId?: string,
  ) => {
    if (!originalSchedulesRef.current) return;

    const input = values && buildAddScheduleInput(track, values, managedScheduleId);
    if (!input) {
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
      return;
    }

    const result = addScheduleOptimistically(originalSchedulesRef.current, input);
    if (result.success) {
      writeSchedules(result.schedules);
      setChangedScheduleIds(getChangedScheduleIds(originalSchedulesRef.current, result.schedules));
    } else {
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
    }
  };

  const handleAddScheduleSubmit = async (
    track: TimetableScheduleTrack,
    values: AddScheduleFormValues,
    managedScheduleId?: string,
  ): Promise<boolean> => {
    if (!originalSchedulesRef.current || !timetableId) return false;
    const input = buildAddScheduleInput(track, values, managedScheduleId);
    if (!input) return false;

    const result = addScheduleOptimistically(originalSchedulesRef.current, input);

    if (result.success === false) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: ADD_SCHEDULE_ERROR_MESSAGES[result.error] });
      return false;
    }

    // Keep the optimistic preview showing while the PATCH is in flight.
    writeSchedules(result.schedules);

    try {
      const response = await submitTimetableUpdate(result.blocks);
      isCommittedRef.current = true;
      queryClient.setQueryData([QueryKeys.timetable, getBrandId(), timetableId], response);
      return true;
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst saving the schedule.' });
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
      return false;
    }
  };

  const openScheduleTray = (track: TimetableScheduleTrack, day: DayOfWeek, managedSchedule?: ManagedSchedule) => {
    if (!timetable || isMenuSetsLoading) return;

    // If a tray was already open with an uncommitted preview (e.g. the user pressed + on
    // another row without closing it first), `timetable.schedules` from this render may still
    // reflect that preview rather than the true original - fall back to the ref instead.
    const trueOriginalSchedules = originalSchedulesRef.current ?? timetable.schedules;
    revertUncommittedPreview();
    originalSchedulesRef.current = trueOriginalSchedules;
    setChangedScheduleIds(new Set());
    setInvalidScheduleIds(new Set());
    trayInstanceIdRef.current += 1;

    openTray(
      <AddScheduleTrayContent
        key={`${track}-${day}-${trayInstanceIdRef.current}`}
        track={track}
        initialDay={day}
        managedSchedule={managedSchedule}
        menus={selectableMenus}
        isMenusLoading={isMenusLoading}
        menuSets={selectableMenuSets}
        isMenuSetsLoading={isMenuSetsLoading}
        priceSchemes={selectablePriceSchemes}
        isPriceSchemesLoading={isPriceSchemesLoading}
        onPreview={(values) => handleAddSchedulePreview(track, values, managedSchedule?.scheduleId)}
        onSubmit={(values) => handleAddScheduleSubmit(track, values, managedSchedule?.scheduleId)}
      />,
      { title: managedSchedule ? 'Manage time slot' : `Add ${capitalise(TRACK_LABELS[track])}` },
    );
  };

  const handleAddSchedule = (track: TimetableScheduleTrack, day: DayOfWeek) => openScheduleTray(track, day);

  const handleManageSchedule = (
    track: TimetableScheduleTrack,
    day: DayOfWeek,
    { scheduleId, entityId, name, startTime, endTime }: ITimetableDaySchedule,
  ) => openScheduleTray(track, day, { scheduleId, entityId, entityName: name, startTime, endTime });

  const openRemovePrompt = (daySchedule: ITimetableDaySchedule) => {
    const schedule = timetable?.schedules.find((candidate) => candidate.schedule_id === daySchedule.scheduleId);
    if (schedule) openRemoval({ kind: 'timeslot', schedule });
  };

  const openTimetableDetailsTray = () => {
    if (!timetable || isMenuSetsLoading) return;

    originalDetailsRef.current = {
      default_menu_id: timetable.default_menu_id,
      default_menu_set_id: timetable.default_menu_set_id ?? null,
      default_price_scheme_id: timetable.default_price_scheme_id,
      ranges: timetable.ranges,
      schedules: timetable.schedules,
    };
    isDetailsCommittedRef.current = false;
    setChangedScheduleIds(new Set());
    setInvalidScheduleIds(new Set());

    const onClose = (options?: ICloseTimetableDetailsTrayOptions) => {
      if (options?.updated) isDetailsCommittedRef.current = true;
      closeTray();
    };

    const handleDetailsPreview = (details: ITimetableDetailsPreview) => {
      const original = originalDetailsRef.current;
      if (!original) return;

      // The form's default menu pick, whichever of a menu or a menu set it is.
      const defaultMenuId = details.default_menu_set_id ?? details.default_menu_id ?? '';
      const previewSchedules = buildTimetableDetailsPreviewSchedules(original.schedules, {
        defaultMenuId,
        defaultPriceSchemeId: details.default_price_scheme_id,
        ranges: details.ranges,
        menus,
        menuSets,
        priceSchemes,
      });

      writeTimetableDetails(details);
      writeSchedules(previewSchedules);
      setChangedScheduleIds(
        new Set([
          ...getChangedScheduleIds(original.schedules, previewSchedules),
          // Picking an already-scheduled entity as the new default promotes its existing block
          // to Default without reassigning any entity, so the content diff above sees nothing
          // different - this catches that case too.
          ...getDefaultStatusChangedScheduleIds(original.schedules, previewSchedules),
        ]),
      );
      setInvalidScheduleIds(
        getInvalidDefaultScheduleIds(original.schedules, {
          defaultMenuId,
          defaultPriceSchemeId: details.default_price_scheme_id,
          ranges: details.ranges,
          menus,
          menuSets,
        }),
      );
    };

    openTray(
      <TimetableDetailsTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        onClose={onClose}
        onPreview={handleDetailsPreview}
      />,
      { title: 'Timetable Details' },
    );
  };

  // The tray keeps the element it opened with, so call the latest opener at click time.
  const openTimetableDetailsTrayRef = useRef(openTimetableDetailsTray);
  openTimetableDetailsTrayRef.current = openTimetableDetailsTray;

  const openTimetableEntitiesTray = () => {
    if (!timetable) return;

    openTray(
      <TimetableEntitiesTrayContent
        timetableId={timetable.timetable_id}
        canEdit={canUpdateTimetables}
        onChangeDefault={() => openTimetableDetailsTrayRef.current()}
        onRemoveEntity={(group) => openRemoval({ kind: 'entity', group })}
        onClose={closeTray}
      />,
      { title: 'Timetable Details' },
    );
  };

  // "Edit details" on the timetables list navigates here with this state so the details tray
  // opens straight away. Waits for menus/menu sets/price schemes too, since the live preview needs them.
  const shouldOpenDetailsTray = (location.state as ITimetableLocationState | null)?.openDetailsTray;
  const isReadyToOpenDetailsTray = !!timetable && !isMenusLoading && !isMenuSetsLoading && !isPriceSchemesLoading;
  const hasAutoOpenedDetailsTrayRef = useRef(false);
  useEffect(() => {
    if (!shouldOpenDetailsTray || !isReadyToOpenDetailsTray || hasAutoOpenedDetailsTrayRef.current) return;
    hasAutoOpenedDetailsTrayRef.current = true;
    // Cleared straight from browser history so a refresh doesn't reopen the tray. Clearing it
    // with navigate() instead would change location.key, which remounts the page via the
    // RouterErrorBoundary - and the unmount cleanup above would close the tray again.
    window.history.replaceState({ ...window.history.state, usr: null }, '');
    if (canUpdateTimetables) openTimetableDetailsTray();
  }, [shouldOpenDetailsTray, isReadyToOpenDetailsTray]);

  const deletePrompt = useDeleteTimetablePrompt({
    timetableId: timetableId ?? '',
    timetableName: timetable?.name,
    genericErrorMessage: DELETE_ERROR_MESSAGE,
    onDeleted: () => navigate(Routes.TIMETABLES),
  });

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  if (!timetableId || !timetable) return <Navigate to={Routes.TIMETABLES} replace />;

  const openDuplicateTray = () => {
    const onClose = (options?: ICloseDuplicateTrayOptions) => {
      closeTray();
      if (options?.duplicated) navigate(Routes.TIMETABLES);
    };

    openTray(
      <DuplicateTimetableTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        sourceName={timetable.name}
        onClose={onClose}
      />,
      { title: 'Duplicate timetable' },
    );
  };

  const openRenameTray = () => {
    openTray(
      <RenameTimetableTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        currentName={timetable.name}
        onClose={() => closeTray()}
      />,
      { title: 'Edit Name' },
    );
  };

  const removalCopy = removalTarget && getRemovePromptCopy(removalTarget);

  return (
    <div className={styles['page-container']}>
      <Prompt
        promptHeading={removalCopy?.heading ?? ''}
        closePrompt={closeRemovePrompt}
        open={isRemovePromptOpen}
        buttonHandler={confirmRemoval}
        buttonText={removalCopy?.buttonText}
        buttonColour="tomato"
        backButton={true}
        backButtonText="Cancel"
        size="small"
        isSubmitting={isRemoving}
      >
        <div className={styles['remove-prompt-body']}>
          <Typography size="medium">{removalCopy?.body}</Typography>
          <Typography size="small">This action cannot be undone.</Typography>
        </div>
      </Prompt>
      <BreadcrumbHeading
        breadcrumbs={[
          { path: Routes.TIMETABLES, label: 'Timetables' },
          { path: `${Routes.TIMETABLES}/${timetable.timetable_id}`, label: timetable.name },
        ]}
        subtitle={`Last updated at ${formatDate(new Date(timetable.update_date))}`}
        primaryButton={
          isTrayOpen ? undefined : (
            <PrimaryButton onClick={() => setIsPublishModalOpen(true)} icon={Export} disabled={!canPublishTimetables}>
              Publish
            </PrimaryButton>
          )
        }
        secondaryButton={
          isTrayOpen ? undefined : (
            <SecondaryButton onClick={handleValidate} icon={ValidationIcon} disabled={isValidationInProgress}>
              Validate
            </SecondaryButton>
          )
        }
        optionMenuItems={
          isTrayOpen
            ? undefined
            : [
                {
                  id: 'edit-timetable-name',
                  name: 'Edit name',
                  icon: TextIcon,
                  isDisabled: !canUpdateTimetables,
                  onOptionClick: openRenameTray,
                },
                {
                  id: 'edit-timetable-details',
                  name: 'Edit details',
                  icon: EditIcon,
                  isDisabled: !canUpdateTimetables || isMenuSetsLoading,
                  onOptionClick: openTimetableDetailsTray,
                },
                {
                  id: 'view-validation-report',
                  name: 'View Validation Report',
                  icon: ValidationIcon,
                  isDisabled: !canViewValidationReport,
                  onOptionClick: () => setIsReportModalOpen(true),
                },
                {
                  id: 'duplicate-timetable',
                  name: 'Duplicate',
                  icon: DuplicateIcon,
                  isDisabled: !canCreateTimetables,
                  onOptionClick: openDuplicateTray,
                },
                {
                  id: 'delete',
                  name: 'Delete',
                  icon: deletePrompt.isCheckingCanDelete ? SpinnerIcon : Bin,
                  isDisabled: deletePrompt.isCheckingCanDelete,
                  persistMenuOnClick: true,
                  hoverColour: 'tomato' as const,
                  onOptionClick: deletePrompt.openDeletePrompt,
                },
              ]
        }
      />
      {rangesEnabled && (
        <div className={styles['ranges']}>
          <Typography size="small" className={styles['label']}>
            {timetable.ranges.length === 0 ? 'No Ranges' : 'Range(s) -'}
          </Typography>
          {timetable.ranges.map((range) => (
            <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
          ))}
        </div>
      )}
      <div className={styles['toolbar']}>
        <RadioSelect
          label="Timetable View"
          name="timetable-view"
          options={[
            {
              options: TIMETABLE_VIEW_OPTIONS,
              selected: timetableView,
              onChange: (id) => setTimetableView(id as TimetableView),
            },
          ]}
        />
        {!isTrayOpen && (
          <SecondaryButton buttonStyle="outline" size="small" icon={ViewIcon} onClick={openTimetableEntitiesTray}>
            View details
          </SecondaryButton>
        )}
      </div>
      <div className={styles['list']}>
        <div className={styles['day-range']}>
          <div className={styles['day-range-start']}>
            <Typography size="small" className={styles['label']}>
              Day Start (00:00)
            </Typography>
            <TimetableTooltip label="Day start information">
              The timetable must cover a 24-hour period, which is determined by your brand&apos;s trading hours. Venue
              open and close are determined by your venue&apos;s opening hours in the Venue Details Page
            </TimetableTooltip>
          </div>
          <Typography size="small" className={`${styles['label']} ${styles['day-range-end']}`}>
            Day End (23:59)
          </Typography>
        </div>
        <div className={styles['days']}>
          {byDay.map((day) => (
            <TimetableDay
              key={day.day}
              day={day}
              view={timetableView}
              onAddSchedule={handleAddSchedule}
              onManageSchedule={handleManageSchedule}
              // Trays keep the menu set list they open with, so wait until it's complete.
              isActionable={canUpdateTimetables && !isTrayOpen && !isMenuSetsLoading}
              changedScheduleIds={changedScheduleIds}
              onDeleteSchedule={openRemovePrompt}
              invalidScheduleIds={invalidScheduleIds}
            />
          ))}
        </div>
      </div>
      {validationReportId && (
        <ValidationReportContainer
          isOpen={isReportModalOpen}
          closeModal={() => setIsReportModalOpen(false)}
          reportId={validationReportId}
          isTimetableReport
        />
      )}
      <TimetablePublishModal
        isOpen={isPublishModalOpen}
        closeModal={() => setIsPublishModalOpen(false)}
        timetableId={timetable.timetable_id}
        timetableName={timetable.name}
      />
      <DeleteEntityPrompts
        entityLabel="timetable"
        entityName={timetable.name}
        unaffectedItems="menu or price scheme"
        deletePrompt={deletePrompt}
      />
    </div>
  );
};

export default Timetable;
